import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import AlertComponent from "../../components/AlertComponent";
import { Context } from "../../Reducers/Store";
import Loading from "../../components/Loading";

const emptyUser = {
  nome: "",
  email: "",
  tipoUsuario: 3,
  gruposIds: [],
};

export default function UsuarioForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state] = useContext(Context);
  const isEdit = !!id;

  const [aba, setAba] = useState("dados");
  const [form, setForm] = useState(emptyUser);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [grupos, setGrupos] = useState([]);

  const isSistema = state?.profile?.tipo_usuario === "Sistema";

  useEffect(() => {
    if (isSistema && !isEdit) {
      setForm((prev) => ({ ...prev, tipoUsuario: 1 }));
    }
  }, [isSistema, isEdit]);

  useEffect(() => {
    let cancelled = false;

    async function carregar() {
      try {
        const gruposRes = await axios.get(`${process.env.REACT_APP_API}/grupos`);

        if (cancelled) return;

        let gruposList = gruposRes.data.data.items.map((g) => ({
          ...g,
          checked: false,
        }));

        if (isEdit) {
          const usuarioRes = await axios.get(`${process.env.REACT_APP_API}/usuario/${id}`);

          if (cancelled) return;

          const data = usuarioRes.data.data;
          const gruposIdsDoUsuario = data.grupos?.map((g) => g.id) ?? [];

          setForm({
            nome: data.nome || "",
            email: data.email || "",
            tipoUsuario: data.tipoUsuario || 3,
            gruposIds: gruposIdsDoUsuario,
          });

          gruposList = gruposList.map((g) => ({
            ...g,
            checked: gruposIdsDoUsuario.includes(g.id),
          }));
        }

        setGrupos(gruposList);
      } catch (err) {
        console.error(err);
        AlertComponent("Erro", "Erro ao carregar dados", { icon: "error" });
      } finally {
        if (!cancelled) {
          setLoadingData(false);
        }
      }
    }

    carregar();

    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const toggleGrupo = (grupoId) => {
    setGrupos((prev) =>
      prev.map((g) =>
        g.id === grupoId ? { ...g, checked: !g.checked } : g
      )
    );
  };

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      gruposIds: grupos.filter((g) => g.checked).map((g) => g.id),
    }));
  }, [grupos]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "tipoUsuario" ? parseInt(value) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        nome: form.nome,
        email: form.email,
        tipoUsuario: form.tipoUsuario,
        gruposIds: form.gruposIds,
      };

      if (isEdit) {
        await axios.put(`${process.env.REACT_APP_API}/usuario/${id}`, payload);
        AlertComponent("Sucesso", "Usuário salvo com sucesso!", { icon: "success" });
      } else {
        await axios.post(`${process.env.REACT_APP_API}/usuario`, payload);
        AlertComponent("Sucesso", "Usuário criado com sucesso!", { icon: "success" });
      }
      navigate(`/usuarios?filtro=${encodeURIComponent(form.nome)}`);
    } catch (error) {
      console.error("Erro ao salvar usuário:", error);
      AlertComponent("Erro", error?.response?.data?.message ?? "Erro ao salvar usuário", { icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  const getTiposUsuarioDisponiveis = () => {
    if (isSistema) {
      return [{ value: 1, label: "Sistema" }];
    }
    return [
      { value: 2, label: "Administrador" },
      { value: 3, label: "Usuário" },
    ];
  };

  if (loadingData) return <Loading />;

  return (
    <div className="w-full">
      {/* HEADER */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-slate-800">
          {isEdit ? "Editar Usuário" : "Novo Usuário"}
        </h1>
        <p className="text-sm text-slate-500">
          {isEdit
            ? "Atualize as informações do usuário"
            : "Preencha as informações para criar um novo usuário"}
        </p>
      </div>

      {/* ABAS */}
      <div className="mb-8 border-b">
        <nav className="flex gap-6">
          {[
            { id: "dados", label: "Dados do Usuário" },
            { id: "grupos", label: "Grupos" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setAba(tab.id)}
              className={`pb-3 text-sm font-medium transition ${
                aba === tab.id
                  ? "border-b-2 border-slate-900 text-slate-900"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              {tab.label}
              {tab.id === "grupos" && grupos.filter((g) => g.checked).length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-slate-200">
                  {grupos.filter((g) => g.checked).length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* ABA DADOS */}
      {aba === "dados" && (
        <div className="bg-white p-8 rounded-xl shadow border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome completo
              </label>
              <input
                type="text"
                name="nome"
                value={form.nome}
                onChange={handleChange}
                className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                placeholder="Digite o nome completo"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-mail
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                placeholder="usuario@exemplo.com"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Usuário
              </label>
              <select
                name="tipoUsuario"
                value={form.tipoUsuario}
                onChange={handleChange}
                className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                disabled={loading || (isSistema && getTiposUsuarioDisponiveis().length === 1)}
              >
                {getTiposUsuarioDisponiveis().map((tipo) => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ABA GRUPOS */}
      {aba === "grupos" && (
        <div className="bg-white p-8 rounded-xl shadow border">
          <div className="space-y-3">
            {grupos.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Nenhum grupo cadastrado</p>
            ) : (
              grupos.map((g) => (
                <label
                  key={g.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 cursor-pointer"
                >
                  <span className="font-medium text-slate-700">{g.nome}</span>
                  <input
                    type="checkbox"
                    checked={g.checked}
                    onChange={() => toggleGrupo(g.id)}
                    className="w-4 h-4"
                    disabled={loading}
                  />
                </label>
              ))
            )}
          </div>
        </div>
      )}

      {/* AÇÕES */}
      <div className="mt-10 flex justify-end gap-3 border-t pt-6">
        <button
          type="button"
          onClick={() => navigate("/usuarios")}
          className="px-5 py-2 rounded-md border hover:bg-gray-50 disabled:opacity-50"
          disabled={loading}
        >
          Cancelar
        </button>

        <button
          type="button"
          onClick={handleSubmit}
          className="px-6 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
          disabled={loading}
        >
          {loading ? "Salvando..." : isEdit ? "Salvar alterações" : "Criar usuário"}
        </button>
      </div>
    </div>
  );
}